import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../context/ChatContext';
import './AIChatWidget.css';

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

const AIChatWidget = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    settings, isOpen, setIsOpen, messages, loading, showContacts,
    contactForm, setContactForm, contactSent, navigatedRef, sendMessage, submitContacts, clearChat, openChat,
    pendingFiles, setPendingFiles
  } = useChat();

  const [input, setInput] = useState('');
  const [windowSize, setWindowSize] = useState({ width: 380, height: 580 });
  const messagesEndRef = useRef(null);
  const widgetRef = useRef(null);
  const fileInputRef = useRef(null);

  const posSide = settings?.widgetPosition === 'left' ? 'left' : 'right';

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, loading, showContacts]); // eslint-disable-line

  useEffect(() => {
    if (navigatedRef.current) return;
    if (messages.length < 2) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.role === 'assistant' && lastMsg.content.startsWith('🔗')) {
      const match = lastMsg.content.match(/\[(.+?)\]/);
      if (match && match[1]) {
        navigatedRef.current = true;
        const timer = setTimeout(() => { navigate(match[1]); }, 600);
        return () => clearTimeout(timer);
      }
    }
  }, [messages, navigate, navigatedRef]);

  const handleMouseDown = useCallback((e) => {
    if (isMobile) return;
    if (e.target.closest('.ai-chat-input') || e.target.closest('.ai-chat-close') ||
        e.target.closest('.ai-chat-clear') || e.target.closest('.ai-chat-resizer')) return;
    const rect = widgetRef.current?.getBoundingClientRect();
    if (!rect) return;
    const startX = e.clientX, startY = e.clientY;
    const origLeft = rect.left, origTop = rect.top;
    const onMove = (ev) => {
      if (!widgetRef.current) return;
      widgetRef.current.style.left = (origLeft + ev.clientX - startX) + 'px';
      widgetRef.current.style.top = (origTop + ev.clientY - startY) + 'px';
      widgetRef.current.style.right = 'auto';
      widgetRef.current.style.bottom = 'auto';
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [isMobile]);

  const handleResizeMouseDown = useCallback((e) => {
    if (isMobile) return;
    e.preventDefault();
    const rect = widgetRef.current?.getBoundingClientRect();
    if (!rect) return;
    const startX = e.clientX, startY = e.clientY, startW = rect.width, startH = rect.height;
    const onMove = (ev) => {
      setWindowSize({
        width: Math.max(300, Math.min(startW + ev.clientX - startX, window.innerWidth - 24)),
        height: Math.max(400, Math.min(startH + ev.clientY - startY, window.innerHeight - 24))
      });
    };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [isMobile]);

  const handleFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setPendingFiles(prev => [...prev, ...picked].slice(0, 5));
    e.target.value = '';
  };

  const handleSend = () => {
    if ((!input.trim() && !pendingFiles.length) || loading) return;
    const text = input;
    setInput('');
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  if (!settings || !settings.enabled) return null;

  const floatStyle = {
    backgroundColor: settings.widgetColor || '#3b82f6',
    [posSide]: isMobile ? '12px' : '20px',
    bottom: isMobile ? 'max(12px, env(safe-area-inset-bottom))' : '20px'
  };

  const windowStyle = isMobile
    ? { width: '100%', height: '100%' }
    : { width: windowSize.width, height: windowSize.height, [posSide]: 20, bottom: 20 };

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          className={`ai-chat-button ai-chat-button--${posSide}`}
          onClick={openChat}
          style={floatStyle}
          aria-label="Открыть чат с AI"
        >
          <span className="ai-chat-button-icon">🤖</span>
          <span className="ai-chat-button-text">{isMobile ? 'AI' : 'Чат с AI'}</span>
        </button>
      )}
      {isOpen && (
        <div
          ref={widgetRef}
          className={`ai-chat-window ai-chat-window--${posSide} ${isMobile ? 'ai-chat-window--mobile' : ''}`}
          style={windowStyle}
        >
          <div
            className="ai-chat-header"
            style={{ background: settings.widgetColor || '#3b82f6', cursor: isMobile ? 'default' : 'grab' }}
            onMouseDown={handleMouseDown}
          >
            <div className="ai-chat-header-info">
              <span className="ai-chat-avatar">🤖</span>
              <div>
                <div className="ai-chat-title">AI-консультант</div>
                <div className="ai-chat-status">СЕТЬМОНТАЖ</div>
              </div>
            </div>
            <div className="ai-header-actions">
              <button type="button" className="ai-chat-clear" onClick={clearChat} title="Очистить историю">🔄</button>
              <button type="button" className="ai-chat-close" onClick={() => setIsOpen(false)} aria-label="Закрыть">✕</button>
            </div>
          </div>
          <div className="ai-chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`ai-message ${msg.role}`}>
                {msg.role === 'assistant' && <div className="ai-message-avatar">🤖</div>}
                <div className="ai-message-content">{msg.content}</div>
                {msg.role === 'user' && <div className="ai-message-avatar user-avatar">👤</div>}
              </div>
            ))}
            {loading && (
              <div className="ai-message assistant">
                <div className="ai-message-avatar">🤖</div>
                <div className="ai-message-content typing"><span>.</span><span>.</span><span>.</span></div>
              </div>
            )}
            {showContacts && !contactSent && (
              <div className="ai-contact-form">
                <p className="ai-contact-title">{settings.contactTriggerMessage || 'Оставьте контакты:'}</p>
                <input type="text" placeholder="Имя" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} />
                <input type="tel" placeholder="Телефон" value={contactForm.phone} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} />
                <input type="email" placeholder="Email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} />
                <button type="button" className="ai-contact-btn" onClick={() => submitContacts(contactForm)} disabled={loading}>✉️ Отправить</button>
              </div>
            )}
            {contactSent && (
              <div className="ai-message assistant">
                <div className="ai-message-avatar">✅</div>
                <div className="ai-message-content">Спасибо! Менеджер свяжется с вами в ближайшее время.</div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {pendingFiles.length > 0 && (
            <div className="ai-chat-attachments">
              {pendingFiles.map((f, i) => (
                <span key={`${f.name}-${i}`} className="ai-attach-chip">
                  📎 {f.name}
                  <button type="button" onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))}>×</button>
                </span>
              ))}
            </div>
          )}
          <div className="ai-chat-input">
            <input ref={fileInputRef} type="file" multiple hidden accept=".txt,.csv,.json,.pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx" onChange={handleFilePick} />
            <button
              type="button"
              className="ai-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || contactSent}
              aria-label="Прикрепить файл"
              title="Прикрепить файл"
            >📎</button>
            <input
              type="text"
              placeholder="Введите сообщение..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading || contactSent}
            />
            <button
              type="button"
              className="ai-send-btn"
              onClick={handleSend}
              disabled={(!input.trim() && !pendingFiles.length) || loading || contactSent}
              aria-label="Отправить"
            >➤</button>
          </div>
          {!isMobile && <div className="ai-chat-resizer" onMouseDown={handleResizeMouseDown} />}
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
